import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  BarChart3,
  BriefcaseBusiness,
  UserRoundCog,
  ChevronLeft,
  FolderTree,
  FileText,
  LayoutDashboard,
  ListPlus,
  Lock,
  MonitorSmartphone,
  PackageCheck,
  Printer,
  ShoppingCart,
  ShieldCheck,
  CreditCard,
  ScanLine,
  Settings2,
  ShoppingBag,
  Store,
  Users,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import {
  NavLink,
  useLocation,
} from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import "../../styles/Sidebar.css";
import '../../styles/components/profile-media.css';

const SALES_REPRESENTATIVE_ROLES = ['salesrepresentative', 'salesrepresentatives', 'marketing', 'marketingmanager', 'responsablemarketing'];

const ORDERS_ONLY_ROLES = [
  'waiter',
  'cuisini',

  // Old roles kept only to prevent access issues with existing users.
  'serveur',
  'cuisine',
  'kitchen',
  'cuisiner',
  'cuisinier',
];

const roleTranslationKeys = {
  SUPER_ADMIN: 'roles.superAdmin',
  SALESREPRESENTATIVE: 'roles.salesRepresentative',
  SALES_REPRESENTATIVE: 'roles.salesRepresentative',
  RESTAURANT_MANAGER: 'roles.restaurantManager',
  MANAGER: 'roles.restaurantManager',
  SERVEUR: 'roles.serveur',
  WAITER: 'roles.serveur',
  CUISINE: 'roles.cuisine',
  KITCHEN: 'roles.cuisine',
  CUISINER: 'roles.cuisine',
  CUISINIER: 'roles.cuisine',
  CUISINI: 'roles.cuisine',
};

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const extractRoleValue = (role) => {
  if (!role) return '';

  if (
    typeof role === 'string' ||
    typeof role === 'number'
  ) {
    return role;
  }

  if (typeof role === 'object') {
    return (
      role.name ||
      role.slug ||
      role.code ||
      role.value ||
      role.key ||
      role.role ||
      ''
    );
  }

  return '';
};

const collectRoleValues = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((role) =>
        collectRoleValues(role),
      )
      .filter(Boolean);
  }

  const extracted =
    extractRoleValue(value);

  return extracted
    ? [extracted]
    : [];
};

const getCurrentUserRoles = (user) => {
  const roleSources = [
    user?.roles,
    user?.role,

    user?.data?.roles,
    user?.data?.role,

    user?.user?.roles,
    user?.user?.role,

    user?.account?.roles,
    user?.account?.role,

    user?.profile?.roles,
    user?.profile?.role,
  ];

  const collectedRoles =
    roleSources.flatMap((source) =>
      collectRoleValues(source),
    );

  const uniqueRoles = [];

  collectedRoles.forEach((role) => {
    const normalized =
      normalizeRole(role);

    if (
      normalized &&
      !uniqueRoles.some(
        (existingRole) =>
          normalizeRole(existingRole) ===
          normalized,
      )
    ) {
      uniqueRoles.push(role);
    }
  });

  return uniqueRoles;
};

const isOrdersOnlyRole = (roles) => {
  if (!roles.length) {
    return false;
  }

  return roles.every((role) =>
    ORDERS_ONLY_ROLES.includes(
      normalizeRole(role),
    ),
  );
};

const isSuperAdminRole = (roles) => {
  return roles.some(
    (role) =>
      normalizeRole(role) ===
      'superadmin',
  );
};

const isSalesRepresentativeRole = (roles) => roles.some((role) => SALES_REPRESENTATIVE_ROLES.includes(normalizeRole(role)));

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const getEstablishmentName = (user) => {
  return (
    user?.establishment?.name ||
    user?.establishment?.title ||
    user?.data?.establishment?.name ||
    user?.user?.establishment?.name ||
    user?.restaurant?.name ||
    ''
  );
};

const getEstablishmentSubtitle = (user) => {
  const establishment =
    user?.establishment ||
    user?.data?.establishment ||
    user?.user?.establishment ||
    user?.restaurant ||
    null;

  const businessName =
    user?.business?.name ||
    user?.data?.business?.name ||
    user?.user?.business?.name ||
    '';

  return (
    establishment?.address ||
    establishment?.city ||
    businessName ||
    ''
  );
};

const getInitials = (
  name,
  fallback = 'U',
) => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return fallback;
  }

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const normalizeRoleForTranslation = (role) => {
  return String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '_');
};

function Sidebar({
  collapsed = false,
  mobileOpen = false,
  onCloseMobile,
}) {
  const { t } = useLanguage();

  const {
    user,
    getFeature,
  } = useAuth();

  const location = useLocation();

  const [hoveredGroup, setHoveredGroup] =
    useState(null);

  const flyoutCloseTimerRef = useRef(null);

  const currentRoles =
    useMemo(
      () =>
        getCurrentUserRoles(
          user,
        ),
      [user],
    );

  const ordersOnly =
    isOrdersOnlyRole(
      currentRoles,
    );

  const superAdmin =
    isSuperAdminRole(
      currentRoles,
    );

  const salesRepresentative = !superAdmin && isSalesRepresentativeRole(currentRoles);

  const homePath = salesRepresentative
    ? '/marketing/dashboard'
    : ordersOnly
    ? '/orders'
    : '/dashboard';

  const establishmentName =
    salesRepresentative
      ? translateFallback(t, 'sidebar.salesBrand', 'Lmenu Sales')
      : superAdmin
      ? translateFallback(
          t,
          'sidebar.platformName',
          'Lmenu',
        )
      : getEstablishmentName(user) ||
        translateFallback(
          t,
          'sidebar.establishment',
          'Establishment',
        );

  const establishmentSubtitle =
    salesRepresentative
      ? translateFallback(t, 'sidebar.fieldSalesWorkspace', 'Field sales workspace')
      : superAdmin
      ? translateFallback(
          t,
          'sidebar.platformAdministration',
          'Platform administration',
        )
      : getEstablishmentSubtitle(user) ||
        translateFallback(
          t,
          'sidebar.establishment',
          'Establishment',
        );

  const establishmentInitials =
    getInitials(
      establishmentName,
      superAdmin || salesRepresentative ? 'L' : 'E',
    );

  const establishmentLogoUrl =
    superAdmin || salesRepresentative
      ? ''
      : user?.logo_url ||
        user?.establishment?.logo_url ||
        user?.data?.establishment?.logo_url ||
        user?.user?.establishment?.logo_url ||
        '';

  const currentUserName =
    user?.name ||
    user?.data?.name ||
    user?.user?.name ||
    translateFallback(
      t,
      'header.profile',
      'Profile',
    );

  const currentUserAvatarUrl =
    user?.avatar_url ||
    user?.data?.avatar_url ||
    user?.user?.avatar_url ||
    '';

  const currentUserInitials =
    getInitials(
      currentUserName,
      'U',
    );

  const currentUserRoleLabels =
    currentRoles.map(
      (role) => {
        const normalizedRoleKey =
          normalizeRoleForTranslation(
            role,
          );

        const translatedRoleKey =
          roleTranslationKeys[
            normalizedRoleKey
          ];

        return translatedRoleKey
          ? translateFallback(t, translatedRoleKey, String(role))
          : String(role);
      },
    );

  const currentUserRoleLabel =
    currentUserRoleLabels.length
      ? currentUserRoleLabels.join(' · ')
      : translateFallback(
          t,
          'header.profile',
          'Profile',
        );

  const aiMenuImportFeature =
    getFeature('ai_menu_import');

  const aiMenuImportVisible =
    aiMenuImportFeature?.visible === true;

  const aiMenuImportAvailable =
    aiMenuImportVisible &&
    aiMenuImportFeature?.available === true &&
    aiMenuImportFeature?.locked !== true;

  const aiMenuImportLocked =
    aiMenuImportVisible &&
    !aiMenuImportAvailable;

  const navigationGroups = useMemo(() => {
    const dashboardItem = {
      label: translateFallback(
        t,
        'sidebar.dashboard',
        'Dashboard',
      ),
      icon: LayoutDashboard,
      path: '/dashboard',
    };

    const usersItem = {
      label: translateFallback(
        t,
        'sidebar.users',
        'Users',
      ),
      icon: Users,
      path: '/users',
    };

    const managersItem = {
      label: translateFallback(
        t,
        'sidebar.managers',
        'Managers',
      ),
      icon: UserRoundCog,
      // Reuse the existing user-management page.
      path: '/users',
    };

    const salesRepresentativesItem = {
      label: translateFallback(
        t,
        'sidebar.salesRepresentatives',
        'Sales Representatives',
      ),
      icon: BriefcaseBusiness,
      path: '/sales-representatives',
    };

    const establishmentsItem = {
      label: translateFallback(
        t,
        'sidebar.establishments',
        'Establishments',
      ),
      icon: Store,
      path: '/establishments',
    };

    const platformFeaturesItem = {
      label: translateFallback(
        t,
        'sidebar.platformFeatures',
        'Platform features',
      ),
      icon: Settings2,
      path: '/platform-features',
    };

    const freeTrialRequestsItem = {
      label: translateFallback(
        t,
        'sidebar.freeTrialRequests',
        'Free trial requests',
      ),
      icon: ListPlus,
      path: '/free-trial-requests',
    };

    const legalPagesItem = {
      label: translateFallback(
        t,
        'sidebar.legalPages',
        'Legal pages',
      ),
      icon: FileText,
      path: '/legal-pages',
    };

    const ordersItem = {
      label: translateFallback(
        t,
        'sidebar.orders',
        'Orders',
      ),
      icon: ShoppingBag,
      path: '/orders',
    };

    const qrPrintProductsItem = {
      label: translateFallback(
        t,
        'sidebar.qrPrintProducts',
        'QR products',
      ),
      icon: Printer,
      path: '/qr-print/products',
    };

    const qrPrintOrdersItem = {
      label: translateFallback(
        t,
        'sidebar.qrPrintOrders',
        'Print orders',
      ),
      icon: PackageCheck,
      path: '/qr-print/orders',
    };

    if (superAdmin) {
      return [
        {
          key: 'main',
          label: translateFallback(
            t,
            'sidebar.main',
            'Main',
          ),
          icon: LayoutDashboard,
          items: [
            dashboardItem,
          ],
        },
        {
          key: 'platform-management',
          label: translateFallback(
            t,
            'sidebar.management',
            'Management',
          ),
          icon: Users,
          items: [
            managersItem,
            salesRepresentativesItem,
            establishmentsItem,
            platformFeaturesItem,
            freeTrialRequestsItem,
            legalPagesItem,
          ],
        },
        {
          key: 'platform-tracking',
          label: translateFallback(
            t,
            'sidebar.restaurantTracking',
            'Tracking',
          ),
          icon: Activity,
          items: [
            {
              label: translateFallback(
                t,
                'sidebar.activityTracking',
                'Activity tracking',
              ),
              icon: Activity,
              path: '/activity-tracking',
            },
          ],
        },
        {
          key: 'qr-print',
          label: translateFallback(
            t,
            'sidebar.qrPrint',
            'QR print',
          ),
          icon: Printer,
          items: [
            qrPrintProductsItem,
            qrPrintOrdersItem,
          ],
        },
        // Super Admin verification links share one existing flyout group.
        {
          key: 'sales-verification',
          label: translateFallback(t, 'sidebar.salesVerification', 'Sales verification'),
          icon: ShieldCheck,
          items: [
            { label: translateFallback(t, 'sidebar.approvalOverview', 'Approval overview'), icon: ShieldCheck, path: '/admin/sales-verification' },
            { label: translateFallback(t, 'sidebar.salesSubscriptions', 'Subscriptions'), icon: CreditCard, path: '/admin/sales-verification/subscriptions' },
            { label: translateFallback(t, 'sidebar.qrPrintApprovals', 'QR print approvals'), icon: PackageCheck, path: '/admin/sales-verification/orders' },
          ],
        },
      ];
    }

    if (salesRepresentative) {
      // Each entry is a standalone navigation link, not a flyout group.
      return [
        {
          key: 'field-sales-dashboard',
          label: translateFallback(t, 'sidebar.fieldSales', 'Field sales'),
          icon: LayoutDashboard,
          items: [
            { label: translateFallback(t, 'sidebar.dashboard', 'Dashboard'), icon: LayoutDashboard, path: '/marketing/dashboard' },
          ],
        },
        {
          key: 'field-sales-new-visit',
          label: '',
          icon: ListPlus,
          items: [
            { label: translateFallback(t, 'sidebar.newFieldVisit', 'New Field Visit'), icon: ListPlus, path: '/marketing/field-visits/new' },
          ],
        },
        {
          key: 'field-sales-visits',
          label: '',
          icon: BriefcaseBusiness,
          items: [
            { label: translateFallback(t, 'sidebar.myVisits', 'My Visits'), icon: BriefcaseBusiness, path: '/marketing/visits' },
          ],
        },
      ];
    }

    if (ordersOnly) {
      return [
        {
          key: 'restaurant-tracking',
          label: translateFallback(
            t,
            'sidebar.restaurantTracking',
            'Restaurant tracking',
          ),
          icon: ShoppingBag,
          items: [
            ordersItem,
          ],
        },
      ];
    }

    const menuItems = [
      {
        label: translateFallback(
          t,
          'sidebar.menuDesign',
          'Menus',
        ),
        icon: MonitorSmartphone,
        path: '/menus',
      },
      {
        label: translateFallback(
          t,
          'sidebar.categories',
          'Categories',
        ),
        icon: FolderTree,
        path: '/categories',
      },
      {
        label: translateFallback(
          t,
          'sidebar.products',
          'Products',
        ),
        icon: UtensilsCrossed,
        path: '/products',
      },
      {
        label: translateFallback(
          t,
          'sidebar.supplements',
          'Supplements',
        ),
        icon: ListPlus,
        path: '/supplements',
      },
    ];

    /*
     * The backend completely removes the feature from /auth/me
     * when it must stay hidden.
     *
     * Therefore the item is added only when visible=true.
     */
    if (aiMenuImportVisible) {
      menuItems.push({
        label: translateFallback(
          t,
          'sidebar.importMenu',
          'Import menu',
        ),
        icon: aiMenuImportLocked
          ? Lock
          : ScanLine,
        path: '/menu-import',
        locked: aiMenuImportLocked,
      });
    }

    return [
      {
        key: 'main',
        label: translateFallback(
          t,
          'sidebar.main',
          'Main',
        ),
        icon: LayoutDashboard,
        items: [
          dashboardItem,
        ],
      },
      {
        key: 'menu',
        label: translateFallback(
          t,
          'sidebar.menuManagement',
          'Menu management',
        ),
        icon: UtensilsCrossed,
        items: menuItems,
      },
      {
        key: 'restaurant-tracking',
        label: translateFallback(
          t,
          'sidebar.restaurantTracking',
          'Restaurant tracking',
        ),
        icon: BarChart3,
        items: [
          ordersItem,
          {
            label: translateFallback(
              t,
              'sidebar.menuAnalytics',
              'Menu analytics',
            ),
            icon: BarChart3,
            path: '/menu-analytics',
          },
          {
            label: translateFallback(
              t,
              'sidebar.activityTracking',
              'Activity tracking',
            ),
            icon: Activity,
            path: '/activity-tracking',
          },
        ],
      },
      {
        key: 'qr-print',
        label: translateFallback(
          t,
          'sidebar.qrPrint',
          'QR print',
        ),
        icon: Printer,
        items: [
          qrPrintProductsItem,
          {
            label: translateFallback(
              t,
              'sidebar.qrPrintCart',
              'Print cart',
            ),
            icon: ShoppingCart,
            path: '/qr-print/cart',
          },
          qrPrintOrdersItem,
        ],
      },
      {
        key: 'management',
        label: translateFallback(
          t,
          'sidebar.management',
          'Management',
        ),
        icon: Users,
        items: [
          usersItem,
        ],
      },
    ];
  }, [
    aiMenuImportLocked,
    aiMenuImportVisible,
    ordersOnly,
    salesRepresentative,
    superAdmin,
    t,
  ]);

  const activeFlyoutGroup =
    navigationGroups.find(
      (group) =>
        group.key === hoveredGroup?.key &&
        Array.isArray(group.items) &&
        group.items.length > 1,
    );

  const isGroupActive = (group) => {
    return (group.items || []).some(
      (item) =>
        !item.locked &&
        (
          location.pathname === item.path ||
          location.pathname.startsWith(
            `${item.path}/`,
          )
        ),
    );
  };

  const closeFlyout = () => {
    if (flyoutCloseTimerRef.current) {
      window.clearTimeout(
        flyoutCloseTimerRef.current,
      );
    }

    flyoutCloseTimerRef.current =
      window.setTimeout(() => {
        setHoveredGroup(null);
        flyoutCloseTimerRef.current = null;
      }, 220);
  };

  const keepFlyoutOpen = () => {
    if (flyoutCloseTimerRef.current) {
      window.clearTimeout(
        flyoutCloseTimerRef.current,
      );

      flyoutCloseTimerRef.current = null;
    }
  };

  const showFlyout = (
    group,
    triggerElement,
  ) => {
    if (
      !triggerElement ||
      !Array.isArray(group.items) ||
      group.items.length <= 1
    ) {
      return;
    }

    keepFlyoutOpen();

    const bounds =
      triggerElement.getBoundingClientRect();

    const isRtl =
      document.documentElement.dir === 'rtl';

    const flyoutWidth = 240;
    const viewportPadding = 12;

    const estimatedHeight = Math.min(
      360,
      56 + group.items.length * 48,
    );

    const top = Math.min(
      Math.max(
        bounds.top,
        viewportPadding,
      ),
      Math.max(
        viewportPadding,
        window.innerHeight -
          estimatedHeight -
          viewportPadding,
      ),
    );

    const left = isRtl
      ? Math.max(
          viewportPadding,
          bounds.left -
            flyoutWidth -
            10,
        )
      : Math.min(
          window.innerWidth -
            flyoutWidth -
            viewportPadding,
          bounds.right + 10,
        );

    const bridgeLeft = isRtl
      ? left + flyoutWidth
      : bounds.right;

    const bridgeWidth = isRtl
      ? Math.max(
          12,
          bounds.left - bridgeLeft,
        )
      : Math.max(
          12,
          left - bounds.right,
        );

    setHoveredGroup({
      key: group.key,
      top,
      left,
      triggerTop: bounds.top,
      triggerHeight: bounds.height,
      bridgeLeft,
      bridgeWidth,
    });
  };

  useEffect(() => {
    setHoveredGroup(null);
  }, [
    location.pathname,
    collapsed,
    mobileOpen,
  ]);

  useEffect(() => {
    return () => {
      if (flyoutCloseTimerRef.current) {
        window.clearTimeout(
          flyoutCloseTimerRef.current,
        );
      }
    };
  }, []);

  const FlyoutIcon =
    activeFlyoutGroup?.icon;

  const renderFlyoutItem = (item) => {
    const ItemIcon = item.icon;

    if (item.locked) {
      return (
        <button
          key={item.path}
          type="button"
          className="dashboard-sidebar-flyout__link dashboard-sidebar-flyout__link--locked"
          disabled
          title={translateFallback(
            t,
            'menuImport.lockedDescription',
            'This feature is not enabled for your account.',
          )}
          role="menuitem"
        >
          <ItemIcon size={18} />

          <span>{item.label}</span>

          <Lock size={15} />
        </button>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={() => {
          setHoveredGroup(null);
          onCloseMobile?.();
        }}
        className={({ isActive }) =>
          `dashboard-sidebar-flyout__link ${
            isActive
              ? 'dashboard-sidebar-flyout__link--active'
              : ''
          }`
        }
        role="menuitem"
      >
        <ItemIcon size={18} />

        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      <aside
        className={`dashboard-sidebar ${
          collapsed
            ? 'dashboard-sidebar--collapsed'
            : ''
        } ${
          mobileOpen
            ? 'dashboard-sidebar--mobile-open'
            : ''
        }`}
      >
        <div className="dashboard-sidebar__header">
          <NavLink
            to={homePath}
            className="dashboard-sidebar__brand dashboard-sidebar__establishment"
            onClick={onCloseMobile}
            title={
              collapsed
                ? establishmentName
                : undefined
            }
          >
            <span className="dashboard-sidebar__brand-icon dashboard-sidebar__establishment-logo">
              {establishmentLogoUrl ? (
                <img
                  src={establishmentLogoUrl}
                  alt={establishmentName}
                />
              ) : (
                <span className="dashboard-sidebar__establishment-initials">
                  {establishmentInitials}
                </span>
              )}
            </span>

            {!collapsed && (
              <span className="dashboard-sidebar__brand-details">
                <strong>
                  {establishmentName}
                </strong>

                <small>
                  {establishmentSubtitle}
                </small>
              </span>
            )}
          </NavLink>

          <button
            type="button"
            className="dashboard-sidebar__mobile-close"
            onClick={onCloseMobile}
            aria-label={translateFallback(
              t,
              'common.close',
              'Close',
            )}
          >
            <X size={21} />
          </button>
        </div>

        <nav className="dashboard-sidebar__navigation">
          {navigationGroups.map(
            (group) => {
              const GroupIcon =
                group.icon;

              const groupActive =
                isGroupActive(group);

              if (
                !Array.isArray(group.items) ||
                group.items.length === 0
              ) {
                return null;
              }

              if (
                group.items.length === 1
              ) {
                const item =
                  group.items[0];

                const ItemIcon =
                  item.icon;

                return (
                  <div
                    className="dashboard-sidebar__group"
                    key={group.key}
                  >
                    {!collapsed && group.label && (
                      <p className="dashboard-sidebar__group-title">
                        {group.label}
                      </p>
                    )}

                    <div className="dashboard-sidebar__items">
                      {item.locked ? (
                        <button
                          type="button"
                          disabled
                          className="dashboard-sidebar__link dashboard-sidebar__link--locked"
                          title={
                            collapsed
                              ? item.label
                              : undefined
                          }
                        >
                          <ItemIcon
                            size={20}
                            className="dashboard-sidebar__link-icon"
                          />

                          {!collapsed && (
                            <span className="dashboard-sidebar__link-label">
                              {item.label}
                            </span>
                          )}

                          {!collapsed && (
                            <Lock
                              size={15}
                              className="dashboard-sidebar__link-arrow"
                            />
                          )}
                        </button>
                      ) : (
                        <NavLink
                          to={item.path}
                          onClick={
                            onCloseMobile
                          }
                          className={({
                            isActive,
                          }) =>
                            `dashboard-sidebar__link ${
                              isActive
                                ? 'dashboard-sidebar__link--active'
                                : ''
                            }`
                          }
                          title={
                            collapsed
                              ? item.label
                              : undefined
                          }
                        >
                          <ItemIcon
                            size={20}
                            className="dashboard-sidebar__link-icon"
                          />

                          {!collapsed && (
                            <span className="dashboard-sidebar__link-label">
                              {item.label}
                            </span>
                          )}

                        </NavLink>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  className="dashboard-sidebar__group"
                  key={group.key}
                >
                  {!collapsed && (
                    <p className="dashboard-sidebar__group-title">
                      {group.label}
                    </p>
                  )}

                  <div className="dashboard-sidebar__items">
                    <button
                      type="button"
                      className={`dashboard-sidebar__link dashboard-sidebar__group-button ${
                        groupActive
                          ? 'dashboard-sidebar__link--active'
                          : ''
                      } ${
                        hoveredGroup?.key ===
                        group.key
                          ? 'is-open'
                          : ''
                      }`}
                      onMouseEnter={(
                        event,
                      ) =>
                        showFlyout(
                          group,
                          event.currentTarget,
                        )
                      }
                      onFocus={(event) =>
                        showFlyout(
                          group,
                          event.currentTarget,
                        )
                      }
                      onMouseLeave={
                        closeFlyout
                      }
                      aria-expanded={
                        hoveredGroup?.key ===
                        group.key
                      }
                      title={
                        collapsed
                          ? group.label
                          : undefined
                      }
                    >
                      <GroupIcon
                        size={20}
                        className="dashboard-sidebar__link-icon"
                      />

                      {!collapsed && (
                        <span className="dashboard-sidebar__link-label">
                          {group.label}
                        </span>
                      )}

                      {!collapsed && (
                        <ChevronLeft
                          size={16}
                          className="dashboard-sidebar__link-arrow"
                        />
                      )}
                    </button>
                  </div>
                </div>
              );
            },
          )}
        </nav>

        <div className="dashboard-sidebar__footer">
          <NavLink
            to="/account-settings"
            className="dashboard-sidebar__business dashboard-sidebar__user-card"
            onClick={onCloseMobile}
            title={
              collapsed
                ? currentUserName
                : undefined
            }
          >
            <div className="dashboard-sidebar__business-logo dashboard-sidebar__user-avatar">
              {currentUserAvatarUrl ? (
                <img
                  src={currentUserAvatarUrl}
                  alt={currentUserName}
                />
              ) : (
                currentUserInitials
              )}
            </div>

            {!collapsed && (
              <div className="dashboard-sidebar__business-info dashboard-sidebar__user-info">
                <strong>
                  {currentUserName}
                </strong>

                <span
                  className="dashboard-sidebar__user-roles"
                  title={currentUserRoleLabel}
                >
                  {currentUserRoleLabel}
                </span>
              </div>
            )}
          </NavLink>
        </div>
      </aside>

      {activeFlyoutGroup &&
        hoveredGroup &&
        createPortal(
          <>
            <div
              className="dashboard-sidebar-flyout-bridge"
              style={{
                top: `${hoveredGroup.triggerTop}px`,
                left: `${hoveredGroup.bridgeLeft}px`,
                width: `${hoveredGroup.bridgeWidth}px`,
                height: `${hoveredGroup.triggerHeight}px`,
              }}
              onMouseEnter={
                keepFlyoutOpen
              }
              onMouseLeave={
                closeFlyout
              }
              aria-hidden="true"
            />

            <div
              className="dashboard-sidebar-flyout"
              style={{
                top: `${hoveredGroup.top}px`,
                left: `${hoveredGroup.left}px`,
              }}
              onMouseEnter={
                keepFlyoutOpen
              }
              onMouseLeave={
                closeFlyout
              }
              role="menu"
              aria-label={
                activeFlyoutGroup.label
              }
            >
              <div className="dashboard-sidebar-flyout__header">
                {FlyoutIcon && (
                  <FlyoutIcon
                    size={18}
                  />
                )}

                <strong>
                  {
                    activeFlyoutGroup.label
                  }
                </strong>
              </div>

              <div className="dashboard-sidebar-flyout__links">
                {activeFlyoutGroup.items.map(
                  renderFlyoutItem,
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

export default Sidebar;
